/**
 * CompanyPanel Component (New Design System)
 *
 * Redesigned Company management panel using the new design system components.
 */

import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, FormControlLabel, Checkbox, List, ListItem, ListItemText, Typography } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Design System Components
import {
    colors,
    EntityPageLayout,
    EntityPageHeader,
    InfoCard,
    ActivityCard,
    DraftTextField,
    DraftSelectField,
    ReadOnlyField,
    Button,
} from '@components/design-system';

// Existing utilities and hooks
import { Operation } from '@/types/general';
import { useBcCustomersQuery, useCompanyMutation, useCompanyQuery } from '@features/redux/apis/org';
import { useAuditHistoryQuery } from '@features/redux/apis/audit';
import { Query } from '@features/redux/apis/utils';
import { Validate } from '@utils/validate';
import { DEFAULT_BILLING_OPTIONS } from '@consts/company';
import { navigateToActivity } from '@utils/activityLinks';
import { CompanyEffects } from './CompanyEffects';
import { ConfirmModal } from '@components/dialogs/ConfirmModal';
import { AppRoutes } from '@consts/routes';

import type { ManageProps } from '../Panel';
import type { RootState } from '@features/redux/store';

// Draft state setup
const useCompanies = Query.withDraft(useCompanyQuery, {
    Reseller: false,
    CompanyName: '',
    Inactive: false,
    CustomerID: null,
    SalesRepTrackingEnabled: true,
    ...DEFAULT_BILLING_OPTIONS,
});

export const CompanyPanel: React.FC<ManageProps> = ({ operation, id: companyId, onSuccess }) => {
    const navigate = useNavigate();
    const { role } = useSelector(({ auth }: RootState) => auth.Roles);
    const auditPk = companyId ? `Company#${companyId}` : null;

    const [confirmOpen, setConfirmOpen] = useState(false);

    // Fetch BC customers
    const { data: customers, isFetching: customersLoading } = useBcCustomersQuery({});
    const customerMap = useMemo(
        () =>
            customers?.reduce((acc, { label, value }) => {
                acc[value] ??= label;
                return acc;
            }, {} as Record<string, string>) ?? {},
        [customers]
    );

    const customerOptions = useMemo(() => customers?.map(({ label, value }) => ({ label, value })) ?? [], [customers]);

    // Fetch company data
    const {
        draft,
        data,
        isFetching: queryLoading,
    } = useCompanies(
        { companyId: +companyId! },
        { skip: operation === Operation.Create, refetchOnMountOrArgChange: true }
    );
    const [draftState, setDraftState] = draft;

    // Mutation
    const [mutate, { isLoading: mutationLoading }] = useCompanyMutation({
        fixedCacheKey: operation === Operation.Edit ? 'CompanyMutation' : 'CompanyCreation',
    });

    // Audit history
    const { data: auditData = [] } = useAuditHistoryQuery(
        { entityPk: auditPk! },
        { skip: !auditPk || operation === Operation.Create }
    );

    const handleSubmit = async () => {
        const result = await mutate({
            method: operation === Operation.Create ? 'POST' : 'PATCH',
            company: draftState,
        });

        // Navigate to edit page after successful creation
        if (operation === Operation.Create && 'data' in result && result.data?.CompanyID) {
            navigate(`${AppRoutes.Organizations}?type=company&id=${result.data.CompanyID}`);
        } else {
            onSuccess?.();
        }
    };

    const isLoading = queryLoading || customersLoading || mutationLoading;
    const canEditPrices = role.prices.edit && role.companies.fullAccess;
    const reassigningCustomer = operation === Operation.Edit && draftState.CustomerID !== data?.CustomerID;
    const isValid = Validate.Org.company(draftState);
    const canSave = role.isAdmin && role.stores.edit;

    const pageTitle = operation === Operation.Create ? 'Create New Company' : data?.CompanyName || 'Company';

    return (
        <>
            <CompanyEffects />

            <EntityPageLayout loading={isLoading && !data}>
                {/* Header */}
                <EntityPageHeader
                    entityType='company'
                    title={pageTitle}
                    id={operation !== Operation.Create ? companyId : undefined}
                    icon={<BusinessIcon />}
                    isCreate={operation === Operation.Create}
                    loading={isLoading && !data}
                    onCancel={onSuccess}
                    onSave={
                        canSave
                            ? () => {
                                  if (reassigningCustomer) {
                                      setConfirmOpen(true);
                                      return;
                                  }
                                  handleSubmit();
                              }
                            : undefined
                    }
                    saveDisabled={!isValid}
                    saving={mutationLoading}
                    actions={
                        operation !== Operation.Create && companyId ? (
                            <Button variant='secondary' onClick={() => navigateToActivity('company', companyId)}>
                                View Activity
                            </Button>
                        ) : undefined
                    }
                />

                {/* Company Info Card */}
                <InfoCard title='Company Information' icon={<InfoOutlinedIcon />}>
                    <Grid container spacing={2}>
                        {operation !== Operation.Create && (
                            <ReadOnlyField label='Company ID' value={data?.CompanyID} size={6} mono />
                        )}

                        <DraftTextField
                            label='Company Name'
                            stateKey='CompanyName'
                            draft={draft}
                            disabled={operation === Operation.View}
                            validateFn={Validate.name}
                            helperText={{
                                valid: '',
                                invalid: 'Please provide a valid company name',
                            }}
                            size={operation !== Operation.Create ? 6 : 12}
                            required
                        />

                        {role.isAdmin && (
                            <DraftSelectField
                                label='Customer Name (Business Central)'
                                stateKey='CustomerID'
                                draft={draft}
                                disabled={operation !== Operation.Create && !role.prices.edit}
                                options={customerOptions}
                                size={12}
                                required
                                defaultFirst={false}
                                placeholder='Select a customer...'
                            />
                        )}

                        {operation !== Operation.View && (
                            <Grid item xs={12}>
                                <FormControlLabel
                                    sx={{ userSelect: 'none', ml: 0 }}
                                    checked={Boolean(draftState?.SalesRepTrackingEnabled)}
                                    onChange={({ target }) => {
                                        const checked = (target as HTMLInputElement).checked;
                                        setDraftState((prev) => ({
                                            ...prev,
                                            SalesRepTrackingEnabled: checked ? true : null,
                                        }));
                                    }}
                                    control={
                                        <Checkbox
                                            sx={{
                                                color: colors.neutral[400],
                                                '&.Mui-checked': {
                                                    color: colors.primary[500],
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography
                                            variant='body2'
                                            sx={{ color: colors.neutral[700], fontWeight: 500 }}>
                                            Sales Rep Tracking Enabled
                                        </Typography>
                                    }
                                />
                            </Grid>
                        )}
                    </Grid>
                </InfoCard>

                {/* Audit Log */}
                {auditPk && operation !== Operation.Create && role.isAdmin && auditData.length > 0 && (
                    <ActivityCard audits={auditData} entityPk={auditPk} maxItems={5} />
                )}
            </EntityPageLayout>

            {/* Confirmation Modal */}
            <ConfirmModal
                title='Company Correlation'
                open={confirmOpen}
                setOpen={setConfirmOpen}
                onConfirm={handleSubmit}>
                <Typography>Are you sure you want to associate the following companies?</Typography>

                <List>
                    <ListItem>
                        <ListItemText
                            primary={`Company #${data?.CompanyID} - ${data?.CompanyName}`}
                            secondary='RapidCut'
                        />
                    </ListItem>

                    <ListItem>
                        <ListItemText
                            primary={`Company #${draftState?.CustomerID} - ${
                                customerMap?.[draftState?.CustomerID as string]
                            }`}
                            secondary='Business Central'
                        />
                    </ListItem>
                </List>
            </ConfirmModal>
        </>
    );
};

export default CompanyPanel;
